import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { RowRenderProps } from 'react-table';

import type { Bill, Bills } from '@feathr/blackbox';
import { Table } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { BillListColumns } from './BillListColumns';
import BillTable from './BillTable';

interface IProps {
  filters: Record<string, unknown>;
  isLoading?: boolean;
  filterElements?: JSX.Element[];
  noDataText?: string;
  collection?: Bills;
  buildLineItemUrl?: (eventId: string, campaignId: string) => string;
  initialPagesize?: number;
  isPaginated?: boolean;
}

function BillListTable({
  filters,
  isLoading = false,
  filterElements = [],
  noDataText,
  collection,
  buildLineItemUrl,
  initialPagesize = 12,
  isPaginated = false,
}: Readonly<IProps>): JSX.Element {
  const { Bills } = useStore();
  const { t } = useTranslation();
  const billsCollection = collection ?? Bills;

  const SubComponent = useCallback(
    function BillTableSubComponent({ original }: Readonly<RowRenderProps>): JSX.Element {
      return <BillTable bill={original as Bill} buildLineItemUrl={buildLineItemUrl} />;
    },
    [buildLineItemUrl],
  );

  return (
    <Table<Bill>
      collection={billsCollection}
      columns={BillListColumns}
      filterElements={filterElements}
      filters={filters}
      initialPagesize={initialPagesize}
      initialSort={[{ id: '_id', desc: true }]}
      isLoading={isLoading}
      isPaginated={isPaginated}
      noDataText={noDataText ?? t('No Data')}
      SubComponent={SubComponent}
    />
  );
}

export default observer(BillListTable);
